import { useEffect, useRef } from "react";

import { useIsTransitioning, useLocation, useMatches } from "common/router/hooks";
import { RouteMatch } from "common/router/types";

import { ClientGenerics } from "client/routes/types";

import { sendPageView } from "../utils";

type ClientRouteMatch = RouteMatch<ClientGenerics>;

const resolvePageName = (match: ClientRouteMatch): string | undefined => {
  const pageName = match.route.meta?.pageName;
  if (!pageName) {
    return undefined;
  }
  return typeof pageName === "function" ? pageName(match.params) : pageName;
};

const resolvePageType = (matches: ClientRouteMatch[]): string | undefined => {
  const match = matches.find((m) => m.route.meta?.pageType);
  return match?.route.meta?.pageType;
};

/**
 * Fires a `vetcove_page_view` GTM event on every route change, including the initial page load.
 *
 * This is a custom event (pushed via `sendCustomEvent`) that runs alongside GA4's
 * built-in `page_view` — the `vetcove_` prefix prevents collision. Partners wire up
 * their GTM triggers against `vetcove_page_view` to pick up our `page_name` and
 * `page_type` on both the initial load and all SPA navigations.
 *
 * - Waits for route transition to complete before reading matches
 *   (because @tanstack/react-location updates location eagerly).
 * - Deduplicates by tracking the last fired pathname.
 *
 * Must be mounted inside the router context (e.g., in ClientRouter's Main).
 */
export const usePageViewTracking = () => {
  const location = useLocation();
  const isTransitioning = useIsTransitioning();
  const matches = useMatches<ClientGenerics>();

  // Track the last pathname we fired for, to prevent duplicates.
  // Initialize to empty string so the first render always fires.
  const lastFiredPath = useRef<string>("");
  const previousUrl = useRef<string>(document.referrer);

  useEffect(() => {
    // Don't fire while transitioning — matches may be stale
    if (isTransitioning) {
      return;
    }

    // Skip page_view for redirect-only routes — the destination route will fire its own event.
    // Returning early preserves both lastFiredPath and previousUrl so the redirect target
    // dedupes and reports its referrer correctly.
    const leafMatch = matches[matches.length - 1];
    if (leafMatch?.route.meta?.skipPageView) {
      return;
    }

    const currentPath = location.current.pathname;
    const currentHref =
      window.location.origin +
      location.current.pathname +
      location.current.searchStr +
      location.current.hash;

    // Same-path navigation (e.g., query param or hash change): don't fire a duplicate
    // page_view, but keep previousUrl in sync so the next real page change reports the
    // user's actual last URL — including updated query params — as the referrer.
    if (currentPath === lastFiredPath.current) {
      previousUrl.current = currentHref;
      return;
    }

    const referrer = previousUrl.current;

    lastFiredPath.current = currentPath;
    previousUrl.current = currentHref;

    const pageName = resolvePageName(leafMatch);
    const pageType = resolvePageType(matches);

    // pageName and pageType are enforced by makeClientRoutes / ClientModuleRoute for all
    // defined routes. Fallbacks only apply to 404 catch-all pages which have no route metadata.
    sendPageView({
      page_location: currentHref,
      page_referrer: referrer,
      page_title: document.title || currentPath,
      page_name: pageName ?? currentPath,
      page_type: pageType ?? "other",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTransitioning,
    location.current.pathname,
    location.current.searchStr,
    location.current.hash,
    matches,
  ]);
};
